import type { Area, Board, EpicSummary, Status, TicketCard } from "@beacon/core/projects"

/** A Board's columns from the left, each Status with its cards from the top. */
export function columnsOf(board: Board): { status: Status; cards: TicketCard[] }[] {
  return board.statuses.map((status) => ({
    status,
    cards: board.tickets.filter((card) => card.statusId === status.id),
  }))
}

/**
 * A Board grouped by Epic: a lane per Epic in the order the Board gives them, then a lane for the
 * cards in none, left out while it has no card, since a card drags only within its lane and so could
 * never land in it. Each lane has the Board's columns and its own cards in them, in Board order.
 * `withEmpty: false` leaves out the lanes with no card, as a filtered Board does: a filter for one
 * Epic would otherwise list every other Epic, each with nothing in it.
 */
export function lanesOf(
  board: Board,
  { withEmpty = true }: { withEmpty?: boolean } = {},
): { id: string; epic: EpicSummary | null; columns: ReturnType<typeof columnsOf> }[] {
  const lane = (epic: EpicSummary | null) => ({
    id: laneIdOf(epic),
    epic,
    columns: columnsOf({
      ...board,
      tickets: board.tickets.filter((card) => card.epic === (epic?.number ?? null)),
    }),
  })
  const filled = ({ columns }: ReturnType<typeof lane>) => columns.some(({ cards }) => cards.length)
  const lanes = [...board.epics.map(lane), lane(null)]
  return lanes.filter((each) => (withEmpty && each.epic !== null) || filled(each))
}

/** How a viewer's browser names a lane it keeps collapsed: its Epic's number, or `none`. */
export const laneIdOf = (epic: Pick<EpicSummary, "number"> | null) =>
  epic ? String(epic.number) : "none"

/** The lane for cards in no Area, named apart from every Area's id and from the Epic lanes'. */
const noAreaLane = "no-area"

/**
 * A Board grouped by Area: a lane per Area in the Project's order, then a lane for the cards in
 * none, left out while it has no card, each lane with the Board's columns and its own cards in
 * them, in Board order. A lane is named by its Area's id, or `no-area`, which is how the browser
 * keeps it collapsed. `withEmpty: false` leaves out every lane with no card, as a filtered Board
 * does.
 */
export function areaLanesOf(
  board: Board,
  { withEmpty = true }: { withEmpty?: boolean } = {},
): { id: string; area: Area | null; columns: ReturnType<typeof columnsOf> }[] {
  const lane = (area: Area | null) => ({
    id: area?.id ?? noAreaLane,
    area,
    columns: columnsOf({
      ...board,
      tickets: board.tickets.filter((card) => card.areaId === (area?.id ?? null)),
    }),
  })
  const filled = ({ columns }: ReturnType<typeof lane>) => columns.some(({ cards }) => cards.length)
  const lanes = [...board.areas.map(lane), lane(null)]
  return lanes.filter((each) => (withEmpty && each.area !== null) || filled(each))
}

/**
 * A Board grouped by Area then Epic: the Area lanes `areaLanesOf` gives, each holding the Epic
 * lanes `lanesOf` gives for its own cards and Epics, a "No Epic" lane last. An Epic with an Area is
 * under that Area alone. An Epic with no Area is under each Area its cards are in, and under "No
 * Area" for its cards in none, or when it has no card on the Board at all, so it still shows
 * somewhere. The lane for cards in no Area is left out while it holds nothing. A nested lane is
 * named by its Area's lane, then its Epic's (`a-pay/7`, `no-area/none`), which is how the browser
 * keeps it collapsed. `withEmpty: false` leaves out every lane with no card, at both levels.
 */
export function areaEpicLanesOf(
  board: Board,
  { withEmpty = true }: { withEmpty?: boolean } = {},
): { id: string; area: Area | null; lanes: ReturnType<typeof lanesOf> }[] {
  const carded = new Set(board.tickets.map(({ epic }) => epic))
  const lane = (area: Area | null) => {
    const id = area?.id ?? noAreaLane
    const tickets = board.tickets.filter((card) => card.areaId === (area?.id ?? null))
    const hasCardHere = new Set(tickets.map(({ epic }) => epic))
    const epics = board.epics.filter((epic) =>
      epic.areaId === null
        ? hasCardHere.has(epic.number) || (area === null && !carded.has(epic.number))
        : epic.areaId === area?.id,
    )
    const lanes = lanesOf({ ...board, tickets, epics }, { withEmpty }).map((nested) => ({
      ...nested,
      id: `${id}/${nested.id}`,
    }))
    return { id, area, lanes, epics, filled: tickets.length > 0 }
  }
  return [...board.areas.map(lane), lane(null)]
    .filter(
      ({ area, epics, filled }) => (withEmpty && (area !== null || epics.length > 0)) || filled,
    )
    .map(({ id, area, lanes }) => ({ id, area, lanes }))
}

/** Which lanes to collapse and which to open, as one change to what the browser keeps. */
export type LaneChange = { collapse: string[]; expand: string[] }

/**
 * Folding a grouped Board's lanes all at once, for an overview: `fold` collapses the innermost
 * lanes (the Epic lanes inside Area lanes, or every lane of a Board grouped one level deep) and
 * opens the Area lanes around them, so each shows its Epics' heads and progress; `unfold` opens
 * every lane. `folded` is whether the Board is folded so now, which the toggle offers to undo.
 */
export function laneFoldOf(
  board: Board,
  grouping: Exclude<BoardGrouping, "none">,
  options: { withEmpty: boolean },
  collapsed: ReadonlySet<string>,
): { folded: boolean; fold: LaneChange; unfold: LaneChange } {
  const [outer, innermost] =
    grouping === "area-epic"
      ? (() => {
          const lanes = areaEpicLanesOf(board, options)
          return [
            lanes.map(({ id }) => id),
            lanes.flatMap(({ lanes }) => lanes.map(({ id }) => id)),
          ]
        })()
      : [
          [],
          (grouping === "epic" ? lanesOf(board, options) : areaLanesOf(board, options)).map(
            ({ id }) => id,
          ),
        ]
  return {
    folded:
      innermost.length > 0 &&
      innermost.every((id) => collapsed.has(id)) &&
      !outer.some((id) => collapsed.has(id)),
    fold: { collapse: innermost, expand: outer },
    unfold: { collapse: [], expand: [...outer, ...innermost] },
  }
}

/**
 * The Epic a card is in, as its chip names it: by its key and title from the Board's Epics, or by
 * the key its number makes when the Board doesn't list it (a completed Epic left off). None for a
 * card in no Epic.
 */
export function epicOfCard(
  card: Pick<TicketCard, "epic">,
  projectKey: string,
  epics: readonly Pick<EpicSummary, "number" | "key" | "title">[],
): { key: string; title?: string } | undefined {
  if (card.epic === null) return undefined
  const epic = epics.find(({ number }) => number === card.epic)
  return epic ? { key: epic.key, title: epic.title } : { key: `${projectKey}-${card.epic}` }
}

/** Where a ticket's page is, by its Project's key and its number. */
export function ticketPath(key: string, number: number) {
  return `/projects/${key}/${number}`
}

/** Where a ticket's page is, by its key, such as `WEB-3`; none for what isn't a key. */
export function pathOfKey(key: string) {
  const [, projectKey, number] = /^([A-Z]+)-(\d+)$/.exec(key) ?? []
  return projectKey && number ? ticketPath(projectKey, Number(number)) : undefined
}

/**
 * The filters a Board's URL may name once, each as `projects` reads it. Its Labels it names apart,
 * as a repeated `label`.
 */
export const boardFilters = [
  "assignee",
  "type",
  "priority",
  "size",
  "text",
  "epic",
  "area",
] as const

export type BoardFilter = (typeof boardFilters)[number]

/** The filters a Board's URL names, leaving out any set to nothing. */
export function boardFiltersIn(params: URLSearchParams) {
  const filters: Partial<Record<BoardFilter, string>> = {}
  for (const filter of boardFilters) {
    const value = params.get(filter)
    if (value) filters[filter] = value
  }
  return filters
}

/** A Board's URL with one filter set, or cleared when set to nothing, and the rest kept. */
export function withBoardFilter(
  params: URLSearchParams,
  filter: BoardFilter,
  value: string | undefined,
) {
  const next = new URLSearchParams(params)
  if (value) next.set(filter, value)
  else next.delete(filter)
  return next
}

/** The parameter a Board's URL repeats, once for each Label it is filtered to. */
const labelParam = "label"

/** The Labels a Board's URL filters it to, by id, each once, in the order it names them. */
export function boardLabelsIn(params: URLSearchParams) {
  return [...new Set(params.getAll(labelParam).filter(Boolean))]
}

/**
 * A Board's URL filtered to the Labels named, each in a `label` of its own, or to none, and the
 * rest kept.
 */
export function withBoardLabels(params: URLSearchParams, labelIds: readonly string[]) {
  const next = new URLSearchParams(params)
  next.delete(labelParam)
  for (const id of labelIds) next.append(labelParam, id)
  return next
}

/**
 * A Board's URL with every filter cleared, its Labels and the text search too, and its view
 * options kept.
 */
export function withoutBoardFilters(params: URLSearchParams) {
  const next = new URLSearchParams(params)
  for (const filter of [...boardFilters, labelParam]) next.delete(filter)
  return next
}

/**
 * How a Board's cards are laid out: in columns alone, in a lane per Epic or per Area, or in a lane
 * per Area holding a lane per Epic.
 */
export type BoardGrouping = "none" | "epic" | "area" | "area-epic"

const groupings: readonly BoardGrouping[] = ["none", "epic", "area", "area-epic"]

/**
 * How a Board is laid out and what it includes beyond its filters, as its Display menu sets them:
 * its grouping, completed Epics left out (which only a Board whose lanes hold Epics does), and
 * cards that closed more than 14 days ago put back.
 */
export type BoardDisplay = { grouping: BoardGrouping; hidingCompleted: boolean; older: boolean }

/** The URL parameter each switch in the Display menu is kept in, and the value it has while on. */
const switchParams = {
  hidingCompleted: ["completed", "hide"],
  older: ["older", "true"],
} as const satisfies Record<Exclude<keyof BoardDisplay, "grouping">, readonly [string, string]>

/**
 * A Board's Display options as its URL keeps them: a grouping as `group=epic`, `group=area` or
 * `group=area-epic`.
 */
export function boardDisplayIn(params: URLSearchParams): BoardDisplay {
  const on = ([name, value]: readonly [string, string]) => params.get(name) === value
  const group = params.get("group")
  return {
    grouping: groupings.find((grouping) => grouping === group) ?? "none",
    hidingCompleted: on(switchParams.hidingCompleted),
    older: on(switchParams.older),
  }
}

/**
 * A Board's URL with Display options changed, each dropped from it while off (no grouping too), and
 * the rest kept.
 */
export function withBoardDisplay(params: URLSearchParams, change: Partial<BoardDisplay>) {
  const next = new URLSearchParams(params)
  const { grouping, ...switches } = change
  if (grouping === "none") next.delete("group")
  else if (grouping) next.set("group", grouping)
  for (const [option, on] of Object.entries(switches) as [keyof typeof switchParams, boolean][]) {
    const [name, value] = switchParams[option]
    if (on) next.set(name, value)
    else next.delete(name)
  }
  return next
}

/** The URL parameters a Board's Display options are kept in. */
const displayParams = ["group", ...Object.values(switchParams).map(([name]) => name)]

/**
 * A Board's Display as its viewer's browser keeps it: the URL parameters that say it, nothing for
 * the default.
 */
export const keptDisplayOf = (display: BoardDisplay) =>
  withBoardDisplay(new URLSearchParams(), display).toString()

/**
 * A Board's URL with the Display its viewer's browser keeps (`keptDisplayOf`) put in, when the URL
 * names no Display option; a URL that names one, as a shared link does, is left as it says.
 */
export function withKeptDisplay(params: URLSearchParams, kept: string | null) {
  if (!kept || displayParams.some((name) => params.has(name))) return params
  return withBoardDisplay(params, boardDisplayIn(new URLSearchParams(kept)))
}

/**
 * The grouping a Board is shown in: the one its URL asks for, but on a Project with no Areas none
 * for a grouping by Area and Epic alone for one by Area then Epic, so a link kept from before its
 * Areas went still shows the Board.
 */
export function shownGrouping(grouping: BoardGrouping, areas: readonly Area[]): BoardGrouping {
  if (areas.length > 0) return grouping
  return grouping === "area" ? "none" : grouping === "area-epic" ? "epic" : grouping
}

/** Whether a grouping's lanes hold Epics, so leaving completed Epics out changes the Board. */
export const nestsEpics = (grouping: BoardGrouping) =>
  grouping === "epic" || grouping === "area-epic"

/**
 * Whether the Board is asked for without completed Epics and their cards: only while its lanes
 * hold Epics, and the Display menu turned them off.
 */
export const leavesOutCompletedEpics = ({ grouping, hidingCompleted }: BoardDisplay) =>
  hidingCompleted && nestsEpics(grouping)

/**
 * Whether a Board's Display differs from the default in what it shows, which its Display button
 * marks. Leaving completed Epics out counts only while the lanes hold Epics, the one layout it
 * changes, and grouping marks the button anyway; otherwise `completed=hide` stays in the URL for
 * when the Board is grouped by Epic again, and the button doesn't claim a change it doesn't make.
 */
export const isDisplayChanged = ({ grouping, older }: BoardDisplay) => grouping !== "none" || older

/**
 * Whether any filter is in force on a Board: one its URL names once, or a Label it is filtered
 * to. `labelIds` are the Project's own among those the URL names, since `projects` leaves any other
 * out of the filter.
 */
export const isBoardFiltered = (
  filters: Partial<Record<BoardFilter, string>>,
  labelIds: readonly string[],
) => Object.keys(filters).length > 0 || labelIds.length > 0

/**
 * Why a Board has no card to show, if it has none: `filtered` when filters are in force, so
 * clearing them may bring cards back; `new` for a Project with nothing in it yet, not even an
 * Epic. A Project with Epics but no card keeps its empty columns, ready for the first.
 *
 * A Board leaves off cards that closed more than 14 days ago, so a Project whose every ticket did
 * reads as new too; the Board can't tell the two apart without asking for them all, and the
 * Display menu puts them back.
 */
export function emptinessOf(
  board: Pick<Board, "tickets" | "epics">,
  filtered: boolean,
): "filtered" | "new" | undefined {
  if (board.tickets.length > 0) return undefined
  if (filtered) return "filtered"
  return board.epics.length === 0 ? "new" : undefined
}
